import * as ramda from 'ramda';
import { createSelector } from 'reselect';

// Safe to ignore: store.getState() is only called at runtime, not during module initialization
import store from '../../../reducers/storeRegistration'; // eslint-disable-line import/no-cycle
import { checkPermission, makeResource } from '../../../../behaviors/src/lib/permissions';
import { currentFormTypeIdentifierSelector } from '../../editor/selectors/formTypeSelectors';
const getUser = () => {
  const user = store.getState().login.user;
  return user || {};
};

/**
 *  Grant access if any of the supplied permissions are present
 */
const getIsPermittedWithAnyOfThesePermissions = function getIsPermittedWithAnyOfThesePermissions(mustHaveOneOfThese) {
  let userIn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const user = userIn !== null ? userIn : getUser();
  const permissions = user.permissions ? user.permissions.general || [] : [];
  // if there is at least one permission in common - allow access
  if (ramda.intersection(permissions, mustHaveOneOfThese || []).length) {
    return true;
  }
  return false;
};

/**
 *  Grant access if none of the supplied permissions are present
 */
const getIsPermittedWithNoneOfThesePermissions = function getIsPermittedWithNoneOfThesePermissions(mustNotHaveAnyOfThese) {
  let userIn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const user = userIn !== null ? userIn : getUser();
  const permissions = user.permissions ? user.permissions.general || [] : [];

  // console.log('NO perms', user.permissions.general);
  // if there is at least one common permission - deny access
  if (ramda.intersection(permissions, mustNotHaveAnyOfThese || []).length) {
    return false;
  }
  return true;
};

/**
 *  Grant access if the report type has edit permission.
 */
const hasReportTypePermissions = function hasReportTypePermissions(reportTypeIdentifier, permission) {
  let userIn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const user = userIn !== null ? JSON.parse(JSON.stringify(userIn)) : getUser();
  const userperms = user.permissions;
  let permissions = [];
  if (userperms && userperms.reportTypes && userperms.reportTypes[reportTypeIdentifier]) {
    permissions = userperms.reportTypes[reportTypeIdentifier];
  }
  // if there is at least one common permission - deny access
  return permissions.indexOf(permission) > -1;
};

/**
 *  Grant access if the identifier is in the list of summaries permissions. If no identifier is
 *  passed, access will be granted if the user has any summaries permissions.
 */
const hasSummaryPermissions = summaryIdentifier => {
  const _getUser = getUser(),
    _getUser$permissions$ = _getUser.permissions.summaries,
    summaries = _getUser$permissions$ === void 0 ? [] : _getUser$permissions$;
  let allowedPermissions = [];
  if (!summaryIdentifier) {
    allowedPermissions = summaries;
  } else if (summaries.indexOf(summaryIdentifier) !== -1) {
    allowedPermissions = summaries[summaries.indexOf(summaryIdentifier)];
  }
  return allowedPermissions.length > 0;
};
const checkStatePermissions = function checkStatePermissions() {
  let perms = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let result = false;
  // if not overriden, check the perms property.
  if (perms.include) {
    result = getIsPermittedWithAnyOfThesePermissions(perms.include);
  }
  if (perms.exclude) {
    result = result || getIsPermittedWithNoneOfThesePermissions(perms.exclude);
  }
  return result;
};

// check to see if permission exists in GAP or RTGAP.
const hasReportPermission = (user, permission, reportTypeIdentifier) => {
  // first check general permissions from the GAP file
  if (user.permissions && Array.isArray(user.permissions.general) && user.permissions.general.includes(permission)) {
    return true;
  }

  // if not in general, check report type based permissions.
  if (reportTypeIdentifier !== undefined && user.permissions && user.permissions.reportTypes && Array.isArray(user.permissions.reportTypes[reportTypeIdentifier]) && user.permissions.reportTypes[reportTypeIdentifier].includes(permission)) {
    return true;
  }
  return false;
};
const userPermissionCheckSelector = (resourceIn, permission) => {
  const userSelector = state => {
    var _state$login;
    return (_state$login = state.login) === null || _state$login === void 0 ? void 0 : _state$login.user;
  };
  const userPermissionsSelector = state => {
    var _state$login2;
    return ((_state$login2 = state.login) === null || _state$login2 === void 0 || (_state$login2 = _state$login2.user) === null || _state$login2 === void 0 ? void 0 : _state$login2.permissions) || {};
  };
  return createSelector([userSelector, userPermissionsSelector], (user, userPermissions) => {
    const resource = typeof resourceIn === 'string' ? {
      resourceIdentifier: resourceIn
    } : resourceIn;
    return checkPermission(resource, permission, userPermissions, user);
  });
};
const userSelector = state => {
  var _state$login3;
  return (_state$login3 = state.login) === null || _state$login3 === void 0 ? void 0 : _state$login3.user;
};
const userPermissionsSelector = state => {
  var _state$login4;
  return ((_state$login4 = state.login) === null || _state$login4 === void 0 || (_state$login4 = _state$login4.user) === null || _state$login4 === void 0 ? void 0 : _state$login4.permissions) || {};
};

// should return true or false, not undefined.
const formTypePermissionSelector = permission => createSelector([userSelector, userPermissionsSelector, currentFormTypeIdentifierSelector], (user, userPermissions, formTypeIdentifier) => {
  if (formTypeIdentifier === undefined) {
    return false;
  }
  const resource = makeResource('form', formTypeIdentifier);
  return checkPermission(resource, permission, userPermissions, user) || false;
});
/**
 *  create a selector to pull out the <permission> of the entity ('edit' or 'view' or ???) and
 *  check it against the user permissions.
 */
const permissionSelector = (entity, permission) => {
  // shorthand causes a no-confusing-arrow, which is worse
  /* eslint-disable arrow-body-style */
  const statePermissions = state => {
    return state[entity] && state[entity].permissions ? state[entity].permissions : [];
  };
  /* eslint-enable arrow-body-style */
  const user = getUser();
  const userId = () => user ? user.user_id : null;
  /*
  * <colin@truqcapp.com>
  * Including userId forces the selector to update when a new
  * user is logged in, otherwise local permissions persist.
  * See ticket #151315426 for more details.
  */
  // eslint-disable-next-line no-unused-vars
  return createSelector([statePermissions, userPermissionCheckSelector, userId], (statePerms, checkSelector) => {
    // first check new permissions to see if we have defined this permission.
    const newPerm = checkSelector(entity, permission);
    if (newPerm !== undefined) {
      return newPerm;
    }
    // if not defined in the new way, check the old way
    return checkStatePermissions(statePerms[permission]);
  });
};
const isCreator = id => {
  const user = getUser();
  return user.user_id === id;
};

// Permission Selectors
const editPermissionSelector = entity => permissionSelector(entity, 'edit');
const viewPermissionSelector = entity => permissionSelector(entity, 'view');
export { formTypePermissionSelector, getIsPermittedWithAnyOfThesePermissions, getIsPermittedWithNoneOfThesePermissions, getUser, editPermissionSelector, isCreator, viewPermissionSelector, hasSummaryPermissions, hasReportTypePermissions, hasReportPermission, userPermissionCheckSelector };